// react core
import { useContext, useState, useEffect, useCallback } from "react";

// material design
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

// entzy context
import configEntzy from "components/config/ConfigEntzy";
import { MainContext } from "components/main/MainContext";
import { ActionLoader } from "components/utils/common/CommonLoaders";

// fonts and colors
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell as iconNotifications } from "@fortawesome/pro-duotone-svg-icons";

const settingsList = [
  {
    id: "search-users-new",
    category: "search",
    type: "users",
    subType: "new",
    name: "New member arrivals",
  },
  {
    id: "search-users-update",
    category: "search",
    type: "users",
    subType: "update",
    name: "Member signal updates",
  },
  {
    id: "search-eventuators-new",
    category: "search",
    type: "eventuators",
    subType: "new",
    name: "New eventuators",
  },
  {
    id: "search-eventuators-update",
    category: "search",
    type: "eventuators",
    subType: "update",
    name: "Eventuator signal updates",
  },
];

function TribeSettings(props) {
  const mainContext = useContext(MainContext);

  const [loading, setLoading] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const handlePullNotificationSettings = async () => {
    setLoading(true);
    const pullPromises = settingsList.map((setting) =>
      mainContext.preparePullMemberNotificationSettings({
        settingCategory: setting.category,
        settingType: setting.type,
        settingSubType: setting.subType,
      })
    );
    const responses = await Promise.all(pullPromises);
    setLoading(false);
    responses.forEach((response) => {
      if (response.alert) {
        mainContext.updateAlert(response);
      } else {
        mainContext.pullMemberNotificationSettings(response.data);
      }
    });
  };
  const cbHandlePullNotificationSettings = useCallback(
    handlePullNotificationSettings,
    [mainContext]
  );

  const handleUpdateNotificationSetting = async (setting) => {
    setLoading(true);
    const response = await mainContext.prepareUpdateMemberNotificationSettings(
      setting
    );
    setLoading(false);
    if (response.alert) {
      mainContext.updateAlert(response);
    } else {
      mainContext.updateMemberNotificationSettings(response.data);
    }
  };

  // pull latest settings on load
  useEffect(() => {
    if (!hydrated) {
      setHydrated(true);
      cbHandlePullNotificationSettings();
    }
  }, [hydrated, cbHandlePullNotificationSettings]);

  return (
    <Box className="box-default">
      {mainContext.state.notifications.settings.settingsHydrated && (
        <Box
          className="box-default"
          sx={{
            p: configEntzy.APP_SPACING_MD,
          }}
        >
          <Box
            className="box-default"
            sx={{ pb: configEntzy.APP_SPACING_MD2X }}
          >
            <Typography variant="h1" color="primary">
              <FontAwesomeIcon icon={iconNotifications} transform="up-6" />
            </Typography>
            <Typography variant="h6" color="primary">
              Tribe Notification Settings
            </Typography>
          </Box>
          {loading ? (
            <Box className="box-default">
              <ActionLoader size="sm" />
            </Box>
          ) : (
            <Box className="box-default">
              {settingsList.map((setting, index) => (
                <Box
                  key={setting.id}
                  className="box-default"
                  sx={{
                    pb: configEntzy.APP_SPACING_MD,
                  }}
                >
                  <Box
                    className="box-default"
                    sx={{ p: configEntzy.APP_SPACING_MD }}
                  >
                    <Divider>
                      <Typography variant="body2" className="upper-case">
                        {setting.name}
                      </Typography>
                    </Divider>
                  </Box>

                  <Box className="box-default">
                    <Box className="box-default half-width">
                      <FormControlLabel
                        control={
                          <Switch
                            color="highlight"
                            checked={
                              mainContext.state.notifications.settings.push?.[
                                setting.category
                              ]?.[setting.type]?.[setting.subType]
                            }
                            onChange={() =>
                              handleUpdateNotificationSetting({
                                notificationType: "push",
                                category: setting.category,
                                type: setting.type,
                                subType: setting.subType,
                                value: mainContext.state.notifications.settings
                                  .push?.[setting.category]?.[setting.type]?.[
                                  setting.subType
                                ]
                                  ? false
                                  : true,
                              })
                            }
                          />
                        }
                        label="Push"
                        labelPlacement="end"
                      />
                    </Box>
                    <Box className="box-default half-width">
                      <FormControlLabel
                        control={
                          <Switch
                            color="highlight"
                            checked={
                              mainContext.state.notifications.settings.mail?.[
                                setting.category
                              ]?.[setting.type]?.[setting.subType]
                            }
                            onChange={() =>
                              handleUpdateNotificationSetting({
                                notificationType: "mail",
                                category: setting.category,
                                type: setting.type,
                                subType: setting.subType,
                                value: mainContext.state.notifications.settings
                                  .mail?.[setting.category]?.[setting.type]?.[
                                  setting.subType
                                ]
                                  ? false
                                  : true,
                              })
                            }
                          />
                        }
                        label="Mail"
                        labelPlacement="end"
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default TribeSettings;
